import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { List } from "immutable";
import { AuthService } from "../../../../../auth/auth.service";
import { ArrayHelper } from "../../../../../utilities/contracts/array-helper";
import { DateHelper } from "../../../../../utilities/contracts/date-helper";
import { StringHelper } from "../../../../../utilities/contracts/string-helper";
import { RegExHelper } from "../../../../../utilities/reg-Ex-Helper";
import { ContactMethodType } from "../../cover-letter-template/contact-method-type.enum";
import { RetrievalContactHistory } from "../../retrieval-detail-contact-history.model";

@Component({
  selector: "app-address-detail-contact-history-item",
  templateUrl: "./address-detail-contact-history-item.component.html",
  styleUrls: ["./address-detail-contact-history-item.component.scss"],
})
export class AddressDetailContactHistoryItemComponent implements OnInit {
  @Input() items: List<RetrievalContactHistory>;
  @Input() index: number;
  @Input() showAidLinks = false;
  @Output() retryFax = new EventEmitter<number>();
  organizationName: string;

  readonly visibleAidCount = 5;

  private readonly expandedAidNotes = new Set<number>();

  isCall(contactMethodType: number): boolean {
    return (contactMethodType === ContactMethodType.Call
      || contactMethodType === ContactMethodType.InvoiceCall
      || contactMethodType === ContactMethodType.PendCall
      || contactMethodType === ContactMethodType.ResearchCall
      || contactMethodType === ContactMethodType.AdditionalCollectionCall
      || contactMethodType === ContactMethodType.VendorCall);

  }

  isNotCall(contactMethodType: number): boolean {
    return (contactMethodType === ContactMethodType.Fax
          || contactMethodType === ContactMethodType.Mail
          || contactMethodType === ContactMethodType.Email
          || contactMethodType === ContactMethodType.ProviderAgingEmail
          || contactMethodType === ContactMethodType.ProviderAgingFax
          || contactMethodType === ContactMethodType.Incoming_Call
          || contactMethodType === ContactMethodType.External
          || contactMethodType === ContactMethodType.AutomatedOutreachFax
          || contactMethodType === ContactMethodType.AutomatedOutreachEmail
          || contactMethodType === ContactMethodType.ReRetrievalFax
          || contactMethodType === ContactMethodType.ReRetrievalEmail
          );
  }

  isEmail(contactMethodType: number): boolean {
    return contactMethodType === ContactMethodType.Email;
  }

  isMethodTypeCall(contactMethodType: number): boolean {
    return (contactMethodType === ContactMethodType.Call
       || contactMethodType === ContactMethodType.InvoiceCall
       || contactMethodType === ContactMethodType.PendCall
       || contactMethodType === ContactMethodType.ResearchCall
       || contactMethodType === ContactMethodType.AdditionalCollectionCall
       || contactMethodType === ContactMethodType.Incoming_Call
    );
  }
  isNotes(contactMethodType: number): boolean {
    return (contactMethodType === ContactMethodType.Note
      || contactMethodType === ContactMethodType.VendorNote);
  }

  isVendorNoteWithAid(item: RetrievalContactHistory): boolean {
    return this.showAidLinks
      && item.contactMethodTypeId === ContactMethodType.VendorNote
      && ArrayHelper.isAvailable(item.masterDocumentSourceIds);
  }

  displayedAidIds(item: RetrievalContactHistory): number[] {
    return this.isAidNoteExpanded(item)
      ? item.masterDocumentSourceIds
      : item.masterDocumentSourceIds.slice(0, this.visibleAidCount);
  }

  hiddenAidCount(item: RetrievalContactHistory): number {
    return Math.max(item.masterDocumentSourceIds.length - this.visibleAidCount, 0);
  }

  isAidNoteExpanded(item: RetrievalContactHistory): boolean {
    return this.expandedAidNotes.has(item.contactLogId);
  }

  toggleAidNoteExpanded(item: RetrievalContactHistory): void {
    if (this.isAidNoteExpanded(item)) {
      this.expandedAidNotes.delete(item.contactLogId);
    } else {
      this.expandedAidNotes.add(item.contactLogId);
    }
  }

  getNotes(value: string): string {
    if (StringHelper.isAvailable(value)) {
      const mainNote = value.split("Outstanding Records:");
      const notes = mainNote[0].trim();
      return notes.replace("\\n", "");
    }
    return "";
  }

  retryFailedFax(contactLogId: number) {
    this.retryFax.emit(contactLogId);
  }

  get retryIcon(): string {
    return "fas fa-redo-alt";
  }

  get getData(): List<RetrievalContactHistory> {
    return this.items;
  }

  constructor(private readonly authService: AuthService) { }

  ngOnInit(): void {
    this.organizationName = this.authService.user.organizationName;
  }

  trackByIndex(index, item) {
    return index;
  }

  showCallNotes(item: RetrievalContactHistory): string {
    const rawNote = StringHelper.isAvailable(item.callNote)
      ? item.callNote
      : (this.isCall(item.contactMethodTypeId) && !item.isSuccessful
          ? item.callResult
          : "");

    if (!rawNote) { return ""; }

    const cleaned = rawNote.replace(RegExHelper.dateOffset, "");

    const commitmentKey = "@noteCommitmentDate=";
    const nextCallKey = "@noteNextCallDate=";

    const commitmentIndex = cleaned.indexOf(commitmentKey);
    const nextCallIndex = cleaned.indexOf(nextCallKey);

    // Find the earliest valid index
    const cutIndex = commitmentIndex !== -1 ? commitmentIndex : (nextCallIndex !== -1 ? nextCallIndex : undefined);

    return cutIndex !== undefined ? cleaned.substring(0, cutIndex) : cleaned;
  }

  hasNextCallDate(item: RetrievalContactHistory): boolean {
    const hasNoteDate = StringHelper.isAvailable(item.callNote) && item.callNote.includes("@noteNextCallDate=");

    if (hasNoteDate) { return true; }

    return StringHelper.isAvailable(item.nextCallDate); // Fallback to existing nextCallDate if callnote nextCallDate is not available
  }

  showCommitmentDate(item: RetrievalContactHistory): string {
    const rawNote = StringHelper.isAvailable(item.callNote)
      ? item.callNote
      : (this.isCall(item.contactMethodTypeId) && !item.isSuccessful
          ? item.callResult
          : "");

    if (!rawNote) { return ""; }

    const cleanedNote = rawNote.replace(RegExHelper.dateOffset, "");

    const commitmentKey = "@noteCommitmentDate=";
    const nextCallKey = "@noteNextCallDate=";

    const start = cleanedNote.indexOf(commitmentKey);
    if (start === -1) { return ""; }

    const end = cleanedNote.indexOf(nextCallKey, start);

    const value = end !== -1
      ? cleanedNote.substring(start + commitmentKey.length, end)
      : cleanedNote.substring(start + commitmentKey.length);

    const formatted = DateHelper.format(new Date(value));

    return formatted;
  }

  showNextCallDate(item: RetrievalContactHistory): string {
    const rawNote = StringHelper.isAvailable(item.callNote)
      ? item.callNote
      : (this.isCall(item.contactMethodTypeId) && !item.isSuccessful
          ? item.callResult
          : "");

    if (!rawNote) { return ""; }

    const cleanedNote = rawNote.replace(RegExHelper.dateOffset, "");
    const key = "@noteNextCallDate=";

    const start = cleanedNote.indexOf(key);
    if (start === -1) {
      if (item.nextCallDate) {
        return DateHelper.formatToLocalTime(new Date(item.nextCallDate)).replace("00:00 AM", "");
      }
      return "";
    }

    const value = cleanedNote.substring(start + key.length);
    const formatted = DateHelper.formatToLocalTime(new Date(value)).replace("00:00 AM", "");

    return formatted;
  }

  hasCallNotes(item: RetrievalContactHistory): boolean {
    return StringHelper.isAvailable(item.callNote)
        ? true
        : this.isCall(item.contactMethodTypeId) && !item.isSuccessful;
  }

  hasStatus(item: RetrievalContactHistory): boolean {
    return StringHelper.isAvailable(item.status);
  }

  showStatus(item: RetrievalContactHistory): string {
    return StringHelper.isAvailable(item.callNote) ? `Status: ${item.status}` : `<br>Status: ${item.status}`;
  }

}
